import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { tokens } from '@hotjar/hotsauce';
import { GenericShortListFilter, ListOption } from '@hotjar/global-filters';
import { RELEVANCE_CATEGORY_LIST } from '../../constants/relevance';
import { getRelevanceScoreTranslationKeyByName, areRelevanceScoresAllSelected } from './utils';
import { useLocalFiltersTracking } from './hooks/useLocalFiltersTracking';

import type { HotjarFilters } from '@hotjar/recordings/src/store/list/types';

const Container = styled.div`
  width: fit-content;
  margin-top: -${tokens.spacer.x1};
  margin-bottom: ${tokens.spacer.x3};
`;

export type RelevanceScoreFilterProps = {
  currentValue: string[];
  onHotjarFiltersUpdated: (hotjarFilters: HotjarFilters) => void;
  hotjarFilters: HotjarFilters;
};

export const RelevanceScoreFilter = ({
  currentValue,
  onHotjarFiltersUpdated,
  hotjarFilters,
}: RelevanceScoreFilterProps): JSX.Element => {
  const { t } = useTranslation();
  const track = useLocalFiltersTracking();

  const RELEVANCE_FILTER_OPTIONS: ListOption<string>[] = [
    {
      value: RELEVANCE_CATEGORY_LIST.veryLow,
      label: t(getRelevanceScoreTranslationKeyByName(RELEVANCE_CATEGORY_LIST.veryLow)),
    },
    {
      value: RELEVANCE_CATEGORY_LIST.low,
      label: t(getRelevanceScoreTranslationKeyByName(RELEVANCE_CATEGORY_LIST.low)),
    },
    {
      value: RELEVANCE_CATEGORY_LIST.moderate,
      label: t(getRelevanceScoreTranslationKeyByName(RELEVANCE_CATEGORY_LIST.moderate)),
    },
    {
      value: RELEVANCE_CATEGORY_LIST.high,
      label: t(getRelevanceScoreTranslationKeyByName(RELEVANCE_CATEGORY_LIST.high)),
    },
    {
      value: RELEVANCE_CATEGORY_LIST.veryHigh,
      label: t(getRelevanceScoreTranslationKeyByName(RELEVANCE_CATEGORY_LIST.veryHigh)),
    },
  ];

  const getSelectedOptionsLabel = (scores: string[]): string => {
    if (areRelevanceScoresAllSelected(scores)) {
      return '';
    }
    if (scores.length > 2) {
      return `(${scores.length})`;
    }
    return `is ${scores
      .map((score) => RELEVANCE_FILTER_OPTIONS.find((item) => item.value === score)?.label)
      .join(' or ')}`;
  };

  const onSelectChange = (value: string[] | undefined) => {
    if (!value) {
      return;
    }

    const payload = { ...hotjarFilters, relevanceScore: value };

    onHotjarFiltersUpdated(payload);
    track(payload);
  };

  return (
    <Container>
      <GenericShortListFilter
        filterName={t('common:recordings.list.relevance-score.score')}
        iconName="target_hit"
        optionList={RELEVANCE_FILTER_OPTIONS}
        currentValue={currentValue}
        label2={
          currentValue
            ? getSelectedOptionsLabel(currentValue)
            : t('common:recordings.list.relevance-score.score')
        }
        showDropdownCaret
        isInactive={areRelevanceScoresAllSelected(currentValue)}
        onApply={onSelectChange}
        selectAllLabel={t('common:recordings.list.relevance-score.all-label')}
      />
    </Container>
  );
};
